import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import Nav from "@/components/Nav";
import { CheckCircle, Truck, CurrencyInr, Certificate, Storefront } from "@phosphor-icons/react";

const PRODUCTS = [
  "Business cards", "Flyers & pamphlets", "Menus", "Brochures", "Packaging",
  "Hoardings", "Stationery", "Apparel", "Certificates", "Corporate gifts",
];

const setSEO = (title, desc) => {
  document.title = title;
  let m = document.querySelector('meta[name="description"]');
  if (m) m.setAttribute("content", desc);
  let og = document.querySelector('meta[property="og:title"]');
  if (og) og.setAttribute("content", title);
  let ogd = document.querySelector('meta[property="og:description"]');
  if (ogd) ogd.setAttribute("content", desc);
};

export default function Vendors() {
  const [f, setF] = useState({
    name: "", email: "", phone: "", business_name: "", gst_number: "", city: "",
    products: [], monthly_capacity: "", equipment: "", website: "", delivery_radius_km: "", notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setSEO(
      "Become a Print Vendor — Talaio/AI",
      "Join Talaio/AI's print partner network. Get pre-qualified print jobs (business cards, flyers, menus, packaging) with GST-invoiced payouts within 7 days. Apply now."
    );
  }, []);

  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleProduct = (p) => setF({
    ...f, products: f.products.includes(p) ? f.products.filter(x => x !== p) : [...f.products, p]
  });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/apply/vendor", {
        ...f,
        products: f.products,
        delivery_radius_km: f.delivery_radius_km ? Number(f.delivery_radius_km) : null,
      });
      setSubmitted(true);
      toast.success("Application received. We'll be in touch within 48 hours.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Application failed");
    } finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[var(--pf-bg)]">
        <Nav />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <CheckCircle size={56} weight="fill" className="mx-auto text-emerald-500 mb-6" />
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4" data-testid="vendor-application-success">Application received.</h1>
          <p className="text-lg text-[var(--pf-text-2)] mb-8">
            Thanks {f.name}. We review every application within 48 hours. If you&apos;re a fit, you&apos;ll get an activation email at <strong>{f.email}</strong>.
          </p>
          <Link to="/" className="pf-btn-secondary inline-block">← Back to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--pf-bg)]">
      <Nav />
      {/* Hero */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 pt-16 pb-12">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
          <div className="pf-label mb-4 inline-flex items-center gap-2">
            <Storefront size={14} /> For Print Vendors
          </div>
          <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] max-w-5xl">
            Get print jobs <br className="hidden sm:block" />delivered to your press.
          </h1>
          <p className="mt-6 text-lg sm:text-xl text-[var(--pf-text-2)] max-w-2xl">
            No cold sales. No bidding wars. AI-approved print-ready files land on your dashboard with quantity, delivery address and payment already confirmed.
          </p>
          <a href="#apply" data-testid="vendor-hero-cta" className="pf-btn-primary mt-8 inline-flex">Apply to join →</a>
        </motion.div>
      </section>

      {/* Benefits */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12 grid md:grid-cols-3 gap-6">
        {[
          { icon: Truck, title: "Ready-to-print files", desc: "Every job arrives as a 300 DPI PDF with 3mm bleed, crop marks and CMYK — no back-and-forth with clients." },
          { icon: CurrencyInr, title: "GST-invoiced payouts", desc: "Weekly settlement, TDS compliant, GST invoices auto-generated. No chasing payments." },
          { icon: Certificate, title: "Quality-graded orders", desc: "You choose which product categories and price bands you take. Higher NPS = more premium jobs." },
        ].map(({ icon: Icon, title, desc }, i) => (
          <motion.div key={i} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }} className="border border-[var(--pf-border)] bg-white p-6">
            <Icon size={28} className="text-[var(--pf-brand)] mb-3" />
            <h3 className="font-black text-xl tracking-tight mb-2">{title}</h3>
            <p className="text-sm text-[var(--pf-text-2)] leading-relaxed">{desc}</p>
          </motion.div>
        ))}
      </section>

      {/* Requirements */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        <div className="pf-label mb-3">Requirements</div>
        <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-6">What we look for.</h2>
        <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl">
          {[
            "Valid GSTIN and business registration",
            "Own or partner-controlled digital / offset press",
            "Minimum 3 product categories in your capability sheet",
            "Delivery capability within your declared radius (own or courier partner)",
            "Willingness to hit 24–48h turnaround on standard orders",
            "One printed sample reference we can review",
          ].map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <CheckCircle size={18} weight="fill" className="text-emerald-500 mt-0.5 shrink-0" />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Apply form */}
      <section id="apply" className="max-w-3xl mx-auto px-6 md:px-12 py-16 border-t border-[var(--pf-border)]">
        <div className="pf-label mb-3">Apply</div>
        <h2 className="text-3xl sm:text-5xl font-black tracking-tighter mb-3">Tell us about your press.</h2>
        <p className="text-[var(--pf-text-2)] mb-10">We review every application within 48 hours.</p>

        <form onSubmit={submit} className="space-y-5" data-testid="vendor-apply-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="pf-label block mb-2">Your full name *</label>
              <input required data-testid="vendor-name" className="pf-input" value={f.name} onChange={upd("name")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Business name *</label>
              <input required data-testid="vendor-business" className="pf-input" value={f.business_name} onChange={upd("business_name")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Email *</label>
              <input required type="email" data-testid="vendor-email" className="pf-input" value={f.email} onChange={upd("email")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Phone / WhatsApp *</label>
              <input required data-testid="vendor-phone" placeholder="+91 98765 43210" className="pf-input" value={f.phone} onChange={upd("phone")} />
            </div>
            <div>
              <label className="pf-label block mb-2">City *</label>
              <input required data-testid="vendor-city" className="pf-input" value={f.city} onChange={upd("city")} />
            </div>
            <div>
              <label className="pf-label block mb-2">GSTIN (optional)</label>
              <input data-testid="vendor-gst" className="pf-input" value={f.gst_number} onChange={upd("gst_number")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Monthly capacity</label>
              <input data-testid="vendor-capacity" placeholder="e.g. 5000 pieces / month" className="pf-input" value={f.monthly_capacity} onChange={upd("monthly_capacity")} />
            </div>
            <div>
              <label className="pf-label block mb-2">Delivery radius (km)</label>
              <input type="number" data-testid="vendor-radius" className="pf-input" value={f.delivery_radius_km} onChange={upd("delivery_radius_km")} />
            </div>
          </div>

          <div>
            <label className="pf-label block mb-2">Products you can print</label>
            <div className="flex flex-wrap gap-2">
              {PRODUCTS.map(p => (
                <button type="button" key={p} onClick={() => toggleProduct(p)}
                  data-testid={`vendor-product-${p.toLowerCase().replace(/[^a-z0-9]/g,'-')}`}
                  className={`text-xs uppercase tracking-widest font-bold px-3 py-2 border-2 transition-colors ${
                    f.products.includes(p) ? "bg-[var(--pf-text)] text-white border-[var(--pf-text)]" : "bg-white border-[var(--pf-border)] hover:border-[var(--pf-text)]"
                  }`}>{p}</button>
              ))}
            </div>
          </div>

          <div>
            <label className="pf-label block mb-2">Equipment / press setup</label>
            <textarea data-testid="vendor-equipment" rows={2} placeholder="e.g. Konica C6100 digital press, Heidelberg SM52 offset" className="pf-input resize-none" value={f.equipment} onChange={upd("equipment")} />
          </div>
          <div>
            <label className="pf-label block mb-2">Website / portfolio (optional)</label>
            <input data-testid="vendor-website" placeholder="https://" className="pf-input" value={f.website} onChange={upd("website")} />
          </div>
          <div>
            <label className="pf-label block mb-2">Anything else we should know? (optional)</label>
            <textarea data-testid="vendor-notes" rows={3} className="pf-input resize-none" value={f.notes} onChange={upd("notes")} />
          </div>

          <button type="submit" disabled={loading} data-testid="vendor-submit" className="pf-btn-primary w-full sm:w-auto mt-4 justify-center">
            {loading ? "Sending…" : "Submit application →"}
          </button>
        </form>
      </section>
    </div>
  );
}
